// APFormFieldArray — generic RHF useFieldArray repeat-rows helper (batch-A
// consumers: plan tiers, programme subject groups / grades, questionbank
// subjects). Rows render via a render prop; add/remove chrome is standard.
// Render isolation (AD-kit-polish): the array value is NOT watched at the
// component root — each row subscribes to its own slice via a scoped
// useWatch, so typing in row 3 re-renders row 3 only, not row 40.
import { AddSquareLinear, TrashBinMinimalisticLinear } from '@assessprep/icons/solar';
import { ReactNode, useEffect } from 'react';
import {
  useFieldArray,
  useFormContext,
  useWatch,
  type ArrayPath,
  type Control,
  type FieldArray,
  type FieldValues,
  type Path,
  type RegisterOptions,
  type UseFieldArrayProps,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FormLabel } from '@/components/ui/form';
import { cn } from '@/lib/cn';

export interface APFormFieldArrayRowContext {
  index: number;
  /** Removes this row. */
  remove: () => void;
  /** Whether the standard remove button renders for this row. */
  canRemove: boolean;
  /** Current row value (row-scoped `useWatch` slice). */
  value: any;
}

export interface APFormFieldArrayProps<TFieldValues extends FieldValues = FieldValues> {
  name: ArrayPath<TFieldValues>;
  /** Already-translated section label. */
  label?: ReactNode;
  /** Already-translated add-row button label. */
  addLabel: string;
  /** Factory for a fresh row appended by the add button. */
  newItem: () => FieldArray<TFieldValues, ArrayPath<TFieldValues>>;
  /** Row fields; the standard remove button renders beside them unless
   *  `renderRemove` is false for the row. */
  renderRow: (ctx: APFormFieldArrayRowContext) => ReactNode;
  /** Per-row remove gate (v3 pattern: persisted rows aren't removable).
   *  Defaults to always removable. */
  canRemoveRow?: (value: any, index: number, count: number) => boolean;
  /** Add-button gate (v3 patterns: fixed row sets, max-row caps, read-only
   *  integrations). Defaults to always addable. */
  canAdd?: boolean | ((count: number) => boolean);
  /** Array-level validation (min/max…), validated on submit; the root
   *  message renders under the rows. */
  rules?: UseFieldArrayProps<TFieldValues>['rules'];
  className?: string;
  'data-testid'?: string;
}

interface APFormFieldArrayRowProps {
  arrayName: string;
  index: number;
  count: number;
  control: Control<any>;
  remove: (index: number) => void;
  renderRow: (ctx: APFormFieldArrayRowContext) => ReactNode;
  canRemoveRow?: (value: any, index: number, count: number) => boolean;
  baseTestId: string;
  removeLabel: string;
}

// One row = one subscription. useWatch on `${arrayName}.${index}` re-renders
// this component alone when its own fields change; sibling rows stay quiet.
function APFormFieldArrayRow({
  arrayName,
  index,
  count,
  control,
  remove,
  renderRow,
  canRemoveRow,
  baseTestId,
  removeLabel,
}: APFormFieldArrayRowProps) {
  const value = useWatch({ control, name: `${arrayName}.${index}` });
  const canRemove = canRemoveRow ? canRemoveRow(value, index, count) : true;

  return (
    <div className="flex items-start gap-2" data-testid={`${baseTestId}-row-${index}`}>
      <div className="grid min-w-0 flex-1 gap-2">
        {renderRow({ index, remove: () => remove(index), canRemove, value })}
      </div>
      {canRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={removeLabel}
          onClick={() => remove(index)}
          data-testid={`${baseTestId}-remove-${index}`}
        >
          <TrashBinMinimalisticLinear aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

export function APFormFieldArray<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  addLabel,
  newItem,
  renderRow,
  canRemoveRow,
  canAdd = true,
  rules,
  className,
  'data-testid': testId,
}: APFormFieldArrayProps<TFieldValues>) {
  const { t } = useTranslation();
  const { control, formState } = useFormContext<TFieldValues>();
  const { fields, append, remove } = useFieldArray<TFieldValues>({ control, name, rules });

  // RHF quirk (APL-11850 A1): useFieldArray registers `rules` in a render-phase
  // useMemo, but its unmount cleanup leaves the field with `mount: false` — after
  // any remount (StrictMode replay, tabbed/collapsible form sections) the memo
  // doesn't re-run and the rules silently stop validating on submit while the
  // array is pristine. Re-asserting the registration from an effect restores it.
  useEffect(() => {
    if (rules) {
      control.register(
        name as unknown as Path<TFieldValues>,
        rules as RegisterOptions<TFieldValues>
      );
    }
  }, [control, name, rules]);
  const rootError = (formState.errors as Record<string, any>)[name]?.root?.message;
  const resolvedTestId = testId ?? `field-array-${name}`;

  return (
    <div className={cn('grid gap-2', className)} data-testid={resolvedTestId}>
      {label && <FormLabel asChild>{<span>{label}</span>}</FormLabel>}
      <div className="grid gap-3">
        {fields.map((field, index) => (
          <APFormFieldArrayRow
            key={field.id}
            arrayName={name as string}
            index={index}
            count={fields.length}
            control={control as unknown as Control<any>}
            remove={remove}
            renderRow={renderRow}
            canRemoveRow={canRemoveRow}
            baseTestId={resolvedTestId}
            removeLabel={t('CommonText.remove')}
          />
        ))}
      </div>
      {typeof rootError === 'string' && rootError && (
        <p className="text-sm text-destructive" data-testid={`${resolvedTestId}-error`}>
          {rootError}
        </p>
      )}
      {(typeof canAdd === 'function' ? canAdd(fields.length) : canAdd) && (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append(newItem())}
            data-testid={`${resolvedTestId}-add`}
          >
            <AddSquareLinear aria-hidden="true" />
            {addLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
